import { useState, useCallback } from 'react';
import { toNormalized } from '../helpers/coords';

export function usePolygonDrawing(
    dimension: { width: number; height: number },
    overlayRef: React.RefObject<HTMLDivElement | null>,
    setPendingPolygon: React.Dispatch<React.SetStateAction<Array<{ x: number; y: number }> | null>>,
    setPendingComment: React.Dispatch<React.SetStateAction<string>>,
) {
    const [draftPoints, setDraftPoints] = useState<Array<{ x: number; y: number }>>([]);

    const finishPolygon = useCallback((points: Array<{ x: number; y: number }>) => {
        if (points.length < 3) return;
        setPendingPolygon(points);
        setDraftPoints([]);
        setPendingComment('');
    }, [setPendingPolygon, setPendingComment]);

    const handleContainerClick = useCallback((e: React.MouseEvent) => {
        if (e.detail >= 2) return;
        const overlay = overlayRef.current;
        if (!overlay || !dimension.width || !dimension.height) return;
        const base = overlay.getBoundingClientRect();
        const { nx, ny, px, py } = toNormalized(e.clientX, e.clientY, base, dimension.width, dimension.height);

        setDraftPoints((prev) => {
            if (prev.length >= 3) {
                const [fp] = prev;
                const fpx = fp.x * dimension.width;
                const fpy = fp.y * dimension.height;
                if (Math.hypot(px - fpx, py - fpy) < 14) {
                    finishPolygon(prev);
                    return prev;
                }
            }
            return [...prev, { x: nx, y: ny }];
        });
    }, [dimension, overlayRef, finishPolygon]);

    const handleContainerDoubleClick = useCallback((e: React.MouseEvent) => {
        setDraftPoints((prev) => {
            finishPolygon(prev);
            return prev;
        });
    }, [finishPolygon]);

    return {
        draftPoints,
        setDraftPoints,
        finishPolygon,
        handleContainerClick,
        handleContainerDoubleClick,
    };
}
